import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private _available = LocalStorageService.isLocalStorageAvailable();

  constructor() {
    if (!this._available) {
      console.warn('LocalStorage is not available');
    }
  }

  static isLocalStorageAvailable(): boolean {
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return true
    } catch (err) {
      console.error(err);
      return false
    }
  }

  get(key: string): any {
    if (!this._available) return;
    return localStorage.getItem(key);
  }

  set(key: string, value: any) {
    if (!this._available) return;
    localStorage.setItem(key, value);
  }

  /**
   * Detects a quota-exceeded failure across browsers.
   * Modern browsers throw a `DOMException` named `QuotaExceededError`; some
   * legacy engines only set the numeric `code` 22 (or Firefox's 1014).
   */
  static isQuotaExceeded(err: any): boolean {
    return !!err && (
      err.name === 'QuotaExceededError' ||
      err.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      err.code === 22 ||
      err.code === 1014
    );
  }

  /**
   * Quota-safe write. Returns `true` on success, `false` instead of throwing.
   *
   * On `QuotaExceededError` it invokes `onQuota` (which should shrink whatever
   * the caller is persisting) and retries the write. `onQuota` is called
   * repeatedly until the write succeeds or it returns `false`/`undefined`
   * signalling there is nothing left to drop. Any non-quota error, or an
   * irrecoverable quota error after `onQuota` gives up, returns `false`.
   *
   * @param key   storage key
   * @param value value to persist
   * @param onQuota optional callback run between retries; returns the next
   *               value to attempt, or a falsy value to stop retrying
   */
  setSafe(key: string, value: string, onQuota?: () => string | false | void): boolean {
    if (!this._available) return false;
    let current = value;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        localStorage.setItem(key, current);
        return true;
      } catch (err) {
        if (!LocalStorageService.isQuotaExceeded(err) || !onQuota) {
          console.error(err);
          return false;
        }
        const next = onQuota();
        if (!next) {
          console.error(err);
          return false;
        }
        current = next;
      }
    }
  }

  remove(key: string) {
    if (!this._available) return;
    localStorage.removeItem(key);
  }

  clear() {
    if (!this._available) return;
    localStorage.clear();
  }

  // Local storage memorizes data in a key-value form, this means values ​​are stored as strings. 
  // So, if we want to have a real object (or list) to deal with, we must convert the string to a valid JavaScript object. (JSON)
  public getDataJSON(key: string): any {
    if (!this._available) return null;
    return JSON.parse(localStorage.getItem(key));
  }

  public saveDataJSON(key: string, data: any) {
    if (!this._available) return;
    localStorage.setItem(key, JSON.stringify(data));
  }

}
